import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DocumentTextIcon,
  ChevronDownIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import * as API from "../../api";

/**
 * CGU (Conditions générales de vente) — redesigned to match the Tailwind
 * design system used on Home/Contact/FAQ. Data logic unchanged from the legacy
 * MUI page: same API.CGU() call, same response shape (res.data is an array of
 * { title, description }, description being trusted HTML). A native accordion
 * replaces the MUI Collapse; the HTML body is still rendered with
 * dangerouslySetInnerHTML.
 */

function CguItem({ title, description, isOpen, onToggle, index }) {
  const panelId = `cgu-panel-${index}`;
  const buttonId = `cgu-button-${index}`;
  return (
    <div
      className={`overflow-hidden rounded-2xl border bg-white transition-all duration-200 ${
        isOpen
          ? "border-brand-200 shadow-card-hover"
          : "border-slate-100 shadow-card hover:border-slate-200"
      }`}
    >
      <button
        type="button"
        id={buttonId}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        className="flex w-full items-center gap-4 px-5 py-4 text-start sm:px-6 sm:py-5"
      >
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-bold transition-colors ${
            isOpen ? "bg-brand-500 text-white" : "bg-brand-50 text-brand-500"
          }`}
        >
          {String(index + 1).padStart(2, "0")}
        </span>
        <h3 className="flex-1 font-display text-base font-semibold text-ink sm:text-lg">
          {title}
        </h3>
        <ChevronDownIcon
          className={`h-5 w-5 shrink-0 text-ink-subtle transition-transform duration-300 ${
            isOpen ? "rotate-180 text-brand-500" : ""
          }`}
        />
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className={`grid transition-all duration-300 ease-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div
            className="cgu-prose px-5 pb-5 ps-[4.75rem] text-sm leading-relaxed text-ink-muted sm:px-6 sm:pb-6"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>
      </div>
    </div>
  );
}

const CGU = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    API.CGU()
      .then((res) => {
        if (alive) setData(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log(err);
      })
      .then(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="bg-surface">
      {/* Hero — mirrors Home/Contact hero (eyebrow badge + 2-line title) */}
      <section className="relative overflow-hidden bg-surface-muted">
        <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 lg:pt-16 pb-10 sm:pb-14">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-700 shadow-card border border-slate-200/80">
              {t("cgu.eyebrow", "Informations légales")}
            </span>
            <h1 className="mt-4 font-display font-extrabold text-ink leading-[1.15] tracking-tight text-[1.75rem] sm:text-4xl lg:text-5xl">
              <span className="block text-balance">
                {t("cgu.heroTitle", "Conditions générales")}
              </span>
              <span className="inline-block text-brand-500 mt-1.5">
                {t("cgu.heroTitleAccent", "de vente.")}
              </span>
            </h1>
            <p className="mt-4 text-sm sm:text-base text-ink-muted leading-relaxed">
              {t(
                "cgu.heroSubtitle",
                "La société Tafwija, en tant qu'intermédiaire, accompagne sur cette plateforme de réservation en ligne, visiteurs et prestataires d'activités touristiques."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="relative py-10 sm:py-14 lg:py-16">
        <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8">
          {/* Intro note */}
          <div className="mb-8 flex items-start gap-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-card sm:p-6">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-500/15 text-accent-700">
              <ShieldCheckIcon className="h-5 w-5" />
            </span>
            <p className="text-sm leading-relaxed text-ink-muted">
              {t(
                "cgu.introNote",
                "En utilisant la plateforme Tafwija, vous acceptez les conditions générales de vente détaillées ci-dessous. Nous vous invitons à les lire attentivement."
              )}
            </p>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-16 animate-pulse rounded-2xl border border-slate-100 bg-white shadow-card"
                />
              ))}
            </div>
          ) : data.length === 0 ? (
            <div className="rounded-2xl border border-slate-100 bg-white p-10 text-center shadow-card">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
                <DocumentTextIcon className="h-6 w-6" />
              </span>
              <p className="mt-4 text-sm font-medium text-ink-muted">
                {t("cgu.empty", "Aucun contenu pour le moment.")}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {data.map((item, index) => (
                <CguItem
                  key={index}
                  index={index}
                  title={item.title}
                  description={item.description}
                  isOpen={openIndex === index}
                  onToggle={() =>
                    setOpenIndex((cur) => (cur === index ? -1 : index))
                  }
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CGU;
